// DBM cart identity rebind (Dieselbrook/annique-issues#350).
//
// A consultant who fills her basket BEFORE signing in pays the retail price, and nothing on the
// page tells her. Measured on the live store, same cart token and same signed-in consultant, the
// only variable being when the line was added:
//
//     line added while signed OUT, then signed in ....... R399.00
//     same cart cleared, line re-added while signed IN ... R319.20
//
// Why: the tier-pricing cart transform prices a line from `cart.buyerIdentity.customer`, and that
// binding is only (re)established when the cart is WRITTEN. Signing in changes the chrome — her
// name, the Consultant Hub link, the BMTM banner, voucher selection — but never writes the cart, so
// the line keeps the price it was given when nobody was signed in. The reverse direction needs no
// help: signing OUT drops the session, the transform stops resolving a customer, and the base price
// returns on its own.
//
// It is silent by construction. The transform runs with `blockOnFailure: false`, so a no-op is not
// an error anywhere: no dead letter, no log line, and the cart still shows a confident campaign
// "Discount Amount". 33 storefront orders and R10,168.50 went out this way over four days before a
// human noticed one.
//
// THE FIX, and it is one line of cart traffic: when the customer identity CHANGES between page
// loads, write one private cart attribute. That write is enough to rebind the buyer and re-run the
// transform — verified live: R399.00 -> R319.20 with nothing else touched, no quantity change and
// no line rebuild. Then ask the theme to re-render so she sees the corrected price immediately.
//
// ⛔ Do NOT "improve" this into a per-page-load unconditional cart write. It must fire ONLY on an
// identity change, or every page view writes the cart.
(function () {
  'use strict';

  if (window.__dbmCartIdentityRebind) { return; } // one init even if two tags load us
  window.__dbmCartIdentityRebind = true;

  // The identity this browser last rebound the cart for. '' means signed out.
  var STORE_KEY = 'dbm.cartIdentity';
  // Underscore-prefixed so it stays a PRIVATE cart attribute: it carries to the order for forensics
  // but does not show beside the buyer's own note attributes in the admin.
  var ATTRIBUTE = '_dbm_identity_rebind';
  // A failed write must be retried on the next page, but must never loop forever.
  var ATTEMPT_KEY = 'dbm.cartIdentityAttempts';
  var MAX_ATTEMPTS = 3;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function identity() {
    var v = window.__dbmCartIdentity;
    return (v === null || v === undefined || v === '') ? '' : String(v);
  }

  function readLocal(key) {
    try { return window.localStorage ? localStorage.getItem(key) : null; } catch (e) { return null; }
  }

  function writeLocal(key, value) {
    try { if (window.localStorage) { localStorage.setItem(key, value); } } catch (e) { /* storage blocked */ }
  }

  // The cart drawer is a section; its id is the one the theme re-renders from.
  function drawerSectionId() {
    var el = document.querySelector('cart-drawer');
    var section = el && el.closest ? el.closest('.shopify-section') : null;
    var id = section && section.id ? section.id : '';
    return id.indexOf('shopify-section-') === 0 ? id.slice('shopify-section-'.length) : null;
  }

  // Put the corrected price on screen. The cart PAGE is a full re-render in the theme's own code, so
  // mirror that; everywhere else, hand the drawer the section HTML it just asked for.
  function repaint(cart) {
    try {
      var onCartPage = window.themeVariables
        && window.themeVariables.settings
        && window.themeVariables.settings.pageType === 'cart';
      if (onCartPage) { window.location.reload(); return; }

      var sectionId = drawerSectionId();
      if (sectionId && cart && cart.sections && cart.sections[sectionId]) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'cart:identity-rebind', cart: cart }
        }));
        return;
      }
      // No bundled section (an older drawer, or the markup moved) — fall back to the refresh event,
      // which makes the drawer re-fetch its own section.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {
      // The data is already correct; the next navigation renders it.
    }
  }

  var now = identity();
  var last = readLocal(STORE_KEY);

  // Unchanged — the common case on every page view. Nothing to do, and no cart traffic.
  if (last === now) { return; }

  // Signed OUT (or a first visit with no session). The base price is already the right answer for a
  // shopper with no identity, so there is nothing to rebind: just record where we are.
  if (!now) {
    writeLocal(STORE_KEY, '');
    writeLocal(ATTEMPT_KEY, '0');
    return;
  }

  // A different customer than this browser last rebound for — including signed-out -> signed-in,
  // and one consultant signing in after another on a shared machine.
  var attempts = parseInt(readLocal(ATTEMPT_KEY) || '0', 10);
  if (!(attempts >= 0)) { attempts = 0; }
  if (attempts >= MAX_ATTEMPTS) { return; } // give up quietly rather than write the cart forever

  // Cart on every device: while a saved-cart restore holds window.DBMCartLock, rebind after it instead of
  // racing it (snippets/dbm-cart-sync-config.liquid). A free cart rebinds now, exactly as before.
  function rebind() {
    fetch(cartRoot() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (cart) {
        // An empty cart has nothing to reprice, and every line added from here on is stamped with the
        // buyer present. Latch and stop.
        if (!cart || !cart.item_count) {
          writeLocal(STORE_KEY, now);
          writeLocal(ATTEMPT_KEY, '0');
          return null;
        }

        writeLocal(ATTEMPT_KEY, String(attempts + 1));

        var body = { attributes: {} };
        // The value is not read by anything; the WRITE is the point. It doubles as a forensic marker
        // that this cart was rebound, and for which customer.
        body.attributes[ATTRIBUTE] = now + ':' + Date.now();

        // Ask for the drawer section back in the SAME response. Measured 2026-09-17 on the live store:
        // the write corrected the cart to R319.20 but the open drawer went on showing R399.00 until a
        // hard refresh — a shopper would read that as "the discount did not apply" on a cart that is
        // already correct. `cart:refresh` alone was not enough, so re-render from the bundled sections
        // exactly as the theme's own add-to-cart does (assets/theme.js onCartChange_fn reads
        // event.detail.cart.sections[sectionId] and would throw without it).
        var sectionId = drawerSectionId();
        if (sectionId) {
          body.sections = sectionId;
          body.sections_url = window.location.pathname + window.location.search;
        }

        // `attributes` MERGES — the theme's own cart attributes (dbm.consultant_ref, the pickup
        // selection, dbm_selected_vouchers) survive untouched.
        return fetch(cartRoot() + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        });
      })
      .then(function (r) {
        if (!r) { return null; }                  // empty-cart path already latched
        if (!r.ok) { return null; }               // leave the attempt counter raised; the next page retries
        writeLocal(STORE_KEY, now);
        writeLocal(ATTEMPT_KEY, '0');
        return r.json().catch(function () { return null; });
      })
      .then(function (cart) {
        if (!cart) { return; }
        repaint(cart);
      })
      .catch(function () {
        // Network error: the attempt counter is already raised, so the next page load retries and the
        // cap stops it becoming a write loop.
      });
  }

  var lock = window.DBMCartLock;
  if (lock && typeof lock.busy === 'function' && lock.busy()) { lock.whenIdle(rebind); } else { rebind(); }
})();
